/**
 * Groups for the gate's group picker. `public.groups` RLS
 * (`groups_location_scope`) is permissive-OR'd with an unrestricted
 * `groups_read_authenticated` SELECT policy, so it does not itself narrow
 * results to the caller's location — this explicit `.eq('location_code', …)`
 * is the actual scoping (Task 2.2, docs/plans/2026-07-30-location-login.md).
 *
 * rb3↔rb5 campus: when the location has a sibling
 * (`locations.sibling_location_code`), both locations' groups are fetched
 * unconditionally. GroupPicker and RelockSheet always render the full set.
 * Generated types don't carry `sibling_location_code` yet — narrow local row
 * type, same pattern as locationTeachersService.
 */

import { supabase } from '@/integrations/supabase/client';
import type { LocationSessionGroup } from '../types';

export interface LocationGroupsResult {
  groups: LocationSessionGroup[];
  siblingLocationCode: string | null;
}

interface LocationSiblingRow {
  sibling_location_code: string | null;
}

/** Own location's sibling code, or null. Same source `app_location_codes()`
 * reads (`locations.sibling_location_code`) — exported so the content-write
 * gate can include the sibling building (rb3↔rb5) instead of only the
 * device claim. */
export async function getSiblingLocationCode(locationCode: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('locations')
    .select('sibling_location_code')
    .eq('code', locationCode)
    .maybeSingle();
  if (error) throw error;
  return (data as LocationSiblingRow | null)?.sibling_location_code ?? null;
}

export async function getLocationGroups(locationCode: string): Promise<LocationGroupsResult> {
  const siblingLocationCode = await getSiblingLocationCode(locationCode);
  const codes = siblingLocationCode ? [locationCode, siblingLocationCode] : [locationCode];

  const { data, error } = await supabase
    .from('groups')
    .select('id, name, icon_url, location_code')
    .in('location_code', codes)
    .eq('active', true)
    .order('sort_order');
  if (error) throw error;

  const groups = (data ?? []).map(
    (g: { id: string; name: string; icon_url: string | null; location_code: string }) => ({
      id: g.id,
      name: g.name,
      iconUrl: g.icon_url,
      locationCode: g.location_code,
    }),
  );
  return { groups, siblingLocationCode };
}
